// src/lib/paymentsProvisioning.ts
//
// The browser's half of instrument provisioning.
//
// ============================================================================
// THIS FILE TALKS TO PAYMENTS, NOT TO CRM
// ============================================================================
//
//   CRM backend  ── starts the session ───────────────────▶ Payments
//   browser      ── runs the protocol Payments selected ──▶ provider
//   browser      ── POST .../finalization (Provisioning) ─▶ Payments   ← here
//   browser      ── canonical paymentInstrumentId ────────▶ CRM backend
//
// Finalization goes straight to Payments because the provider's single-use
// token must not pass through CRM. Routing it via a CRM endpoint would put
// card-adjacent material into CRM's request logs, error reports and memory in
// exchange for nothing.
//
// CRM's service execution token is never available here — it does not leave the
// backend. The only credential this module holds is the `provisioningAuthority`
// Payments issued for ONE provisioning, and it travels under its own scheme so
// a service token cannot be presented here by mistake.
import type { ProvisioningBrowserAction } from '../api/paymentsIntegrationApi';

/** Where Payments lives, for the browser's direct finalization call. */
const PAYMENTS_BASE_URL = (import.meta.env.VITE_PAYMENTS_API_BASE_URL as string | undefined) ?? '';

export type FinalizationOutcome =
  | {
      ok: true;
      paymentInstrumentId: string;
      status: string;
      /**
       * 201 = this call created the instrument. 200 = a replayed finalization
       * converged on one that already existed.
       *
       * BOTH ARE SUCCESS and may name the SAME instrument. The caller must not
       * treat 200 as a duplicate to be discarded, nor as a second instrument.
       */
      created: boolean;
    }
  | { ok: false; reason: 'requires_attention' | 'refused' | 'unavailable'; detail?: string };

/**
 * Finalize directly with Payments.
 *
 * `paymentToken` is accepted here ONLY to forward to Payments. It is never
 * returned, never stored, never logged, and never sent to CRM.
 */
export async function finalizeProvisioning(args: {
  provisioningAuthority: string;
  /** Present only for `tokenize_with_public_key`. */
  paymentToken?: string;
  fetchImpl?: typeof fetch;
}): Promise<FinalizationOutcome> {
  const { provisioningAuthority, paymentToken } = args;
  const doFetch = args.fetchImpl ?? fetch;

  if (!PAYMENTS_BASE_URL) {
    return { ok: false, reason: 'unavailable', detail: 'Payments base URL is not configured' };
  }

  let res: Response;
  try {
    res = await doFetch(
      `${PAYMENTS_BASE_URL}/v1/payment-instruments/provisioning-sessions/finalization`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Its own scheme, deliberately not `Bearer`.
          Authorization: `Provisioning ${provisioningAuthority}`,
        },
        body: JSON.stringify(paymentToken ? { paymentToken } : {}),
      }
    );
  } catch {
    return { ok: false, reason: 'unavailable', detail: 'network error' };
  }

  if (res.status === 200 || res.status === 201) {
    let body: { paymentInstrumentId?: unknown; status?: unknown };
    try {
      body = (await res.json()) as typeof body;
    } catch {
      return { ok: false, reason: 'refused', detail: 'malformed response' };
    }
    if (typeof body.paymentInstrumentId !== 'string') {
      return { ok: false, reason: 'refused', detail: 'no paymentInstrumentId' };
    }
    return {
      ok: true,
      paymentInstrumentId: body.paymentInstrumentId,
      status: typeof body.status === 'string' ? body.status : 'unknown',
      created: res.status === 201,
    };
  }

  let code: string | undefined;
  try {
    const body = (await res.json()) as { error?: { code?: unknown } };
    if (typeof body?.error?.code === 'string') code = body.error.code;
  } catch {
    /* a non-JSON refusal is still a refusal */
  }

  if (code === 'provisioning_requires_attention') {
    // The outcome is UNKNOWN, not failed. No association may be created, and
    // nothing may restart automatically — see the dialog's copy.
    return { ok: false, reason: 'requires_attention', detail: code };
  }
  if (res.status >= 500) return { ok: false, reason: 'unavailable', detail: code };
  return { ok: false, reason: 'refused', detail: code };
}

/**
 * A human-readable label for what the browser is being asked to do.
 *
 * Exhaustive over the union: adding a Payments protocol without teaching the UI
 * about it fails to compile here rather than rendering a blank step.
 */
export function describeBrowserAction(action: ProvisioningBrowserAction): string {
  switch (action.kind) {
    case 'confirm_setup_with_client_secret':
      return 'Confirm your card details securely with our payment provider.';
    case 'tokenize_with_public_key':
      return 'Enter your card details securely with our payment provider.';
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}
